import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { isMobileBreakpoint } from '@theme/utilities';

const DRAG_CLOSE_THRESHOLD = 90;
const DRAG_RESISTANCE = 0.85;

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {number} */
  #dragStartY = 0;

  /** @type {number} */
  #dragDistance = 0;

  /** @type {boolean} */
  #isDraggingSheet = false;

  /** @type {boolean} */
  #isAutoOpenPending = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartAddStart, this.#handleCartAddStart);
    document.addEventListener(ThemeEvents.cartAddEnd, this.#handleCartAddEnd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.refs.dialog.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    this.refs.dialog.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
    this.refs.dialog.addEventListener('touchend', this.#handleTouchEnd);
    this.refs.dialog.addEventListener('touchcancel', this.#handleTouchEnd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartAddStart, this.#handleCartAddStart);
    document.removeEventListener(ThemeEvents.cartAddEnd, this.#handleCartAddEnd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.refs.dialog.removeEventListener('touchstart', this.#handleTouchStart);
    this.refs.dialog.removeEventListener('touchmove', this.#handleTouchMove);
    this.refs.dialog.removeEventListener('touchend', this.#handleTouchEnd);
    this.refs.dialog.removeEventListener('touchcancel', this.#handleTouchEnd);
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    if (!isMobileBreakpoint() || !this.refs.dialog?.open || event.touches.length !== 1) return;

    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('.cart-discount details[open] .details-content')) return;

    this.#dragStartY = event.touches[0].clientY;
    this.#dragDistance = 0;
    this.#isDraggingSheet = false;
    this.refs.dialog.style.transition = 'none';
  };

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    if (!isMobileBreakpoint() || !this.refs.dialog?.open || event.touches.length !== 1) return;

    const deltaY = event.touches[0].clientY - this.#dragStartY;
    const target = event.target;
    const scrollableParent = target instanceof Element ? target.closest('.cart-drawer__content, .cart-drawer__items') : null;
    const canScrollUp = scrollableParent instanceof HTMLElement && scrollableParent.scrollTop > 0;

    if (deltaY <= 0 || canScrollUp) return;

    this.#isDraggingSheet = true;
    this.#dragDistance = deltaY * DRAG_RESISTANCE;
    event.preventDefault();
    this.refs.dialog.style.transform = `translateY(${this.#dragDistance}px)`;
  };

  #handleTouchEnd = () => {
    if (!this.#isDraggingSheet) {
      this.refs.dialog?.style.removeProperty('transition');
      return;
    }

    const shouldClose = this.#dragDistance > DRAG_CLOSE_THRESHOLD;

    this.refs.dialog.style.removeProperty('transition');
    requestAnimationFrame(() => {
      this.refs.dialog.style.removeProperty('transform');

      if (shouldClose) {
        this.closeDialog();
      }
    });

    this.#isDraggingSheet = false;
    this.#dragDistance = 0;
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = async (event) => {
    const cartItems = this.refs.dialog.querySelector('cart-items-component');
    const sectionId = cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : undefined;

    if (!(event instanceof CartAddEvent)) {
      const sectionHTML = sectionId ? event.detail.data.sections?.[sectionId] : undefined;
      if (sectionHTML) this.#syncDialogState(sectionHTML);
      return;
    }

    if (event.detail.data.didError) {
      this.#isAutoOpenPending = false;
      return;
    }

    if (sectionId) {
      try {
        const sectionHTML = await sectionRenderer.renderSection(sectionId, {
          cache: false,
          mode: 'hydration',
          injectStylesheet: true,
        });
        this.#syncDialogState(sectionHTML);
      } catch (error) {
        console.error('Failed to refresh cart drawer:', error);
      }
    }

    if (this.hasAttribute('auto-open')) {
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      this.showDialog();
      requestAnimationFrame(() => requestAnimationFrame(() => this.#updateStickyState()));
    }

    this.#isAutoOpenPending = false;
    this.#announceCartCount(event.detail.resource?.item_count);
  };

  #syncDialogState(sectionHTML) {
    const updatedDialog = new DOMParser()
      .parseFromString(sectionHTML, 'text/html')
      .querySelector('[data-testid="cart-drawer-dialog"]');

    if (!updatedDialog) return;

    this.refs.dialog.classList.toggle('cart-drawer--empty', updatedDialog.classList.contains('cart-drawer--empty'));

    const labelledBy = updatedDialog.getAttribute('aria-labelledby');
    if (labelledBy) this.refs.dialog.setAttribute('aria-labelledby', labelledBy);
  }

  #handleCartAddStart = () => {
    if (this.hasAttribute('auto-open')) this.#isAutoOpenPending = true;
  };

  #handleCartAddEnd = () => {
    this.#isAutoOpenPending = false;
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    if (this.#isAutoOpenPending) return;

    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
